import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { MapPin, Globe, Languages, Sun, Bell, Trash2, Download } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { api } from "../api";

const LANGS = [["en","English"],["ms","Malay"],["id","Bahasa Indonesia"],["ta","Tamil"],["zh","Chinese"]];
const VOICE_SPEEDS = [["slow","Slow"],["normal","Normal"],["fast","Fast"]];
const VOICE_GENDER = [["","No preference"],["female","Female"],["male","Male"]];

// Local-storage keys (used until DB columns are migrated)
const LS = {
  country: "dnya_country",
  autoLoc: "dnya_auto_location",
  method: "dnya_prayer_method",
  notifPrayer: "dnya_notif_prayer",
  notifEvents: "dnya_notif_events",
  notifMessages: "dnya_notif_messages",
  voiceSpeed: "dnya_voice_speed",
  voiceGender: "dnya_voice_gender",
};

export default function Settings() {
  const { user, setUser, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const [authorities, setAuthorities] = useState({});
  const [defaultMethod, setDefaultMethod] = useState(3);
  const [primaryLoc, setPrimaryLoc] = useState(null);

  const [autoLoc, setAutoLoc] = useState(() => localStorage.getItem(LS.autoLoc) !== "0");
  const [country, setCountry] = useState(() => localStorage.getItem(LS.country) || "Singapore");
  const [methodOverride, setMethodOverride] = useState(() => localStorage.getItem(LS.method) || "");
  const [notifPrayer, setNotifPrayer] = useState(() => localStorage.getItem(LS.notifPrayer) !== "0");
  const [notifEvents, setNotifEvents] = useState(() => localStorage.getItem(LS.notifEvents) !== "0");
  const [notifMessages, setNotifMessages] = useState(() => localStorage.getItem(LS.notifMessages) !== "0");
  const [city, setCity] = useState("");

  useEffect(() => {
    api.get("/authorities").then((r) => { setAuthorities(r.data.authorities); setDefaultMethod(r.data.default_method); });
    api.get("/location/primary").then((r) => { if (r.data) { setPrimaryLoc(r.data); setCity(r.data.city || ""); if (r.data.country) setCountry(r.data.country); } });
  }, []);

  const persistLocal = (k, v) => localStorage.setItem(k, typeof v === "boolean" ? (v ? "1" : "0") : String(v));

  const savePrefLang = async (lang) => {
    try {
      const r = await api.put("/profile/me", { preferred_language: lang });
      setUser(r.data);
      toast.success("Language updated");
    } catch (e) { toast.error("Could not save language"); }
  };

  const requestGPS = () => {
    if (!navigator.geolocation) return toast.error("Geolocation not supported");
    toast("Bismillah... requesting your location");
    navigator.geolocation.getCurrentPosition(async (pos) => {
      const { latitude, longitude } = pos.coords;
      try {
        const r = await api.post("/location/primary", {
          label: "Current", location_type: "home",
          latitude, longitude, country, is_primary: true,
        });
        setPrimaryLoc(r.data);
        toast.success("Location saved");
      } catch (e) { toast.error("Could not save location"); }
    }, (err) => toast.error("Location denied: " + err.message));
  };

  const saveManualCity = async () => {
    try {
      const r = await api.post("/location/primary", {
        label: city || "Home", location_type: "home",
        latitude: 0, longitude: 0, city, country, is_primary: true,
      });
      setPrimaryLoc(r.data);
      toast.success("City saved");
    } catch (e) { toast.error("Could not save"); }
  };

  const exportData = async () => {
    try {
      const me = await api.get("/auth/me");
      const blob = new Blob([JSON.stringify(me.data, null, 2)], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob); a.download = "dnya-my-data.json"; a.click();
    } catch { toast.error("Could not export"); }
  };

  const deleteAccount = () => {
    if (!window.confirm("This will permanently delete your account and all data. Continue?")) return;
    if (!window.confirm("Are you absolutely sure? This cannot be undone.")) return;
    toast("Account deletion requires email confirmation. We've sent you an email Insha'Allah.");
  };

  if (!user) return null;

  const activeMethod = methodOverride
    ? parseInt(methodOverride)
    : (authorities[country]?.prayer_method ?? defaultMethod);
  const activeAuthority = authorities[country]?.authority_name || "Muslim World League (default)";

  return (
    <div className="dnya-reveal" style={{ maxWidth: 760, margin: "0 auto" }}>
      <h1 className="font-dnya" style={{ fontSize: 28, color: "var(--dnya-high-1)", margin: 0 }}>Settings</h1>
      <div style={{ color: "var(--dnya-high-2)", fontSize: 13, marginTop: 4, marginBottom: 24 }}>Configure your Dnya experience</div>

      <Section icon={MapPin} title="Location">
        <Row label="Auto-detect (GPS)">
          <Toggle checked={autoLoc} onChange={(v) => { setAutoLoc(v); persistLocal(LS.autoLoc, v); if (v) requestGPS(); }} testId="settings-autoloc" />
        </Row>
        {!autoLoc && (
          <Row label="City">
            <div style={{ display: "flex", gap: 8 }}>
              <input data-testid="settings-city" value={city} onChange={(e) => setCity(e.target.value)} placeholder="e.g. Singapore" style={inp} />
              <button onClick={saveManualCity} style={primaryBtn}>Save</button>
            </div>
          </Row>
        )}
        <Row label="Country">
          <select data-testid="settings-country" value={country} onChange={(e) => { setCountry(e.target.value); persistLocal(LS.country, e.target.value); }} style={inp}>
            {Object.keys(authorities).map((c) => <option key={c} value={c}>{c}</option>)}
            <option value="Other">Other</option>
          </select>
        </Row>
        {primaryLoc && (
          <div style={{ color: "var(--dnya-text-muted)", fontSize: 12, fontStyle: "italic" }}>
            Current location: {primaryLoc.city || `(${primaryLoc.latitude?.toFixed(3)}, ${primaryLoc.longitude?.toFixed(3)})`} · {primaryLoc.country || "—"}
          </div>
        )}
      </Section>

      <Section icon={Languages} title="Language">
        <Row label="Preferred Language">
          <select data-testid="settings-language" value={user.preferred_language || "en"} onChange={(e) => savePrefLang(e.target.value)} style={inp}>
            {LANGS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </Row>
      </Section>

      <Section icon={Sun} title="Appearance">
        <Row label="Theme">
          <button data-testid="settings-theme" onClick={toggle} style={{ ...primaryBtn, minWidth: 140 }}>{theme === "dark" ? "Dark mode (active)" : "Light mode (active)"}</button>
        </Row>
      </Section>

      <Section icon={Bell} title="Notifications">
        <Row label="Prayer time reminders"><Toggle checked={notifPrayer} onChange={(v) => { setNotifPrayer(v); persistLocal(LS.notifPrayer, v); }} testId="settings-notif-prayer" /></Row>
        <Row label="Event reminders"><Toggle checked={notifEvents} onChange={(v) => { setNotifEvents(v); persistLocal(LS.notifEvents, v); }} testId="settings-notif-events" /></Row>
        <Row label="Message notifications"><Toggle checked={notifMessages} onChange={(v) => { setNotifMessages(v); persistLocal(LS.notifMessages, v); }} testId="settings-notif-messages" /></Row>
      </Section>

      <Section icon={Globe} title="Prayer Calculation">
        <Row label="Method">
          <select data-testid="settings-method" value={methodOverride} onChange={(e) => { setMethodOverride(e.target.value); persistLocal(LS.method, e.target.value); }} style={inp}>
            <option value="">Auto (by country)</option>
            <option value="3">3 — Muslim World League</option>
            <option value="4">4 — Umm Al-Qura (Makkah)</option>
            <option value="9">9 — Kuwait</option>
            <option value="11">11 — MUIS Singapore</option>
            <option value="20">20 — Kemenag Indonesia</option>
          </select>
        </Row>
        <div style={{ color: "var(--dnya-text-muted)", fontSize: 12, fontStyle: "italic" }}>
          Active method: <strong style={{ color: "var(--dnya-high-1)" }}>{activeMethod}</strong> · {activeAuthority}
        </div>
      </Section>

      <Section icon={Trash2} title="Privacy">
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button data-testid="settings-export" onClick={exportData} style={ghostBtn}><Download size={14} /> Export my data</button>
          <button data-testid="settings-delete" onClick={deleteAccount} style={{ ...primaryBtn, background: "transparent", border: `1px solid var(--dnya-high-1)`, color: "var(--dnya-high-1)" }}>Delete account</button>
        </div>
      </Section>

      <div style={{ marginTop: 28, display: "flex", justifyContent: "flex-end" }}>
        <button onClick={logout} style={primaryBtn}>Logout</button>
      </div>
    </div>
  );
}

const Section = ({ icon: Icon, title, children }) => (
  <div style={{ background: "var(--dnya-card)", border: `1px solid var(--dnya-mosaic-1)`, borderRadius: 14, padding: 20, marginBottom: 16 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14, color: "var(--dnya-high-1)", fontWeight: 600, fontSize: 15 }}>
      <Icon size={18} /> {title}
    </div>
    {children}
  </div>
);

const Row = ({ label, children }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
    <div style={{ color: "var(--dnya-text)", fontSize: 13, fontWeight: 500, minWidth: 160 }}>{label}</div>
    <div style={{ flex: 1, minWidth: 180, display: "flex", justifyContent: "flex-end" }}>{children}</div>
  </div>
);

const Toggle = ({ checked, onChange, testId }) => (
  <button
    data-testid={testId}
    role="switch"
    aria-checked={checked}
    onClick={() => onChange(!checked)}
    style={{
      width: 48, height: 26, borderRadius: 13,
      background: checked ? "var(--dnya-mosaic-1)" : "var(--dnya-bg-2)",
      border: `1px solid var(--dnya-border)`,
      position: "relative", cursor: "pointer", padding: 0,
      transition: "background 150ms ease",
    }}
  >
    <span style={{ position: "absolute", top: 2, left: checked ? 23 : 2, width: 20, height: 20, borderRadius: "50%", background: "var(--dnya-high-1)", transition: "left 150ms ease" }} />
  </button>
);

const inp = { minWidth: 220, padding: "8px 12px", height: 44, borderRadius: 10, border: `1px solid var(--dnya-border)`, background: "var(--dnya-input-bg)", color: "var(--dnya-text)", outline: "none", fontSize: 14 };
const primaryBtn = { padding: "10px 18px", height: 44, borderRadius: 10, background: "var(--dnya-mosaic-1)", color: "#ffffff", border: 0, fontWeight: 600, cursor: "pointer", fontSize: 13 };
const ghostBtn = { padding: "10px 18px", height: 44, borderRadius: 10, background: "transparent", color: "var(--dnya-high-1)", border: `1px solid var(--dnya-border)`, cursor: "pointer", fontSize: 13, display: "inline-flex", alignItems: "center", gap: 6 };
